import { useRef } from 'react';

/**
 * Computes how many secondary items to show next to primary items for the first page
 * (favor primary); remembers the last max while fetching; returns totalExtra count.
 *
 * @param {Object} params
 * @param {number} params.primaryCount
 * @param {number} params.secondaryCount
 * @param {number} [params.primaryTotalCount]
 * @param {number} [params.secondaryTotalCount]
 * @param {number} [params.pageSize]
 * @param {boolean} params.showAll
 * @param {boolean} params.isFetching
 * @returns {{ maxSecondaryToShow: number, totalExtra: number }}
 */
export default function usePairedListComposition({
  primaryCount,
  secondaryCount,
  primaryTotalCount,
  secondaryTotalCount,
  // When provided, represents the total number of items the UI intends to
  // display (primary and secondary). We will favor primary first and then
  // fill the remaining slots with secondary up to this page size.
  pageSize,
  showAll,
  isFetching,
}) {
  const lastMaxSecondaryRef = useRef(0);

  // Determine how many secondary items we want to show for the current view.
  // Prefer primary items first and fill the remainder with secondary.
  let desiredMaxSecondary;
  if (typeof pageSize === 'number') {
    // Desired total visible items is the current page size; allocate remaining
    // slots to secondary after primary are placed.
    const remainingSlots = Math.max(0, pageSize - primaryCount);
    desiredMaxSecondary = Math.min(secondaryCount, remainingSlots);
  } else {
    // Backwards compatibility (when pageSize isn't supplied by caller):
    // original behavior favored primary and only showed secondary when there
    // were more secondary than primary on a page; when showAll is true, show
    // all fetched secondary.
    desiredMaxSecondary = showAll
      ? secondaryCount
      : Math.max(0, secondaryCount - primaryCount);
  }

  if (!isFetching) {
    lastMaxSecondaryRef.current = desiredMaxSecondary;
  }
  const maxSecondaryToShow = lastMaxSecondaryRef.current;

  let totalExtra = (primaryTotalCount || 0) + (secondaryTotalCount || 0);
  totalExtra -= primaryCount + maxSecondaryToShow;

  return { maxSecondaryToShow, totalExtra };
}
